import { useMutation } from '@tanstack/react-query';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import type { ToastData } from '~/hooks/useToast';
import type { CommunityPost } from '~/interfaces/community';

type UpdatePostSuccess = {
  id: string;
  title?: string;
  tiptap_state?: CommunityPost['tiptap_state'];
  updated_at?: string;
  edited_at?: string | null;
  toast?: ToastData;
};

type UpdatePostError = {
  toast: ToastData;
};

export type UpdateCommunityPostResponse = UpdatePostSuccess | UpdatePostError;

const isUpdateCommunityPostResponse = (data: unknown): data is UpdateCommunityPostResponse => {
  if (typeof data !== 'object' || data === null) return false;
  return 'id' in data || 'toast' in data;
};

const useUpdateCommunityPostMutation = () => {
  const ensureCsrfToken = useEnsureCsrfToken();
  return useMutation({
    mutationFn: async (payload: Record<string, string>): Promise<UpdateCommunityPostResponse> => {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch('/update_community_post', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          'X-CSRF-Token': csrfToken,
        },
        body: new URLSearchParams(payload),
      });

      const data: unknown = await response.json();
      if (!isUpdateCommunityPostResponse(data)) throw new Error('composer_invalid_response');

      return data;
    },
  });
};

export default useUpdateCommunityPostMutation;
